import { useState, useEffect } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { FolderTree, Info, ExternalLink } from "lucide-react";

const GROUP_COLORS = {
  Income: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Bills: "bg-amber-50 text-amber-700 border-amber-200",
  Needs: "bg-blue-50 text-blue-700 border-blue-200",
  Wants: "bg-purple-50 text-purple-700 border-purple-200",
};

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await api.get("/categories");
      setCategories(res.data);
    } catch (error) {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  // Group categories
  const categoriesByGroup = {};
  categories.forEach((cat) => {
    if (!categoriesByGroup[cat.group]) categoriesByGroup[cat.group] = [];
    categoriesByGroup[cat.group].push(cat);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in-up">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight text-slate-900">
          Categories
        </h1>
        <p className="text-slate-500 mt-1">
          Budget categories synced from YNAB
        </p>
      </div>

      {/* Info Banner */}
      <Alert className="border-sky-200 bg-sky-50">
        <Info className="h-4 w-4 text-sky-600" />
        <AlertDescription className="text-sky-800">
          <p className="font-medium">Categories are managed in YNAB</p>
          <p className="text-sm mt-1">
            To add, edit, or delete categories, make changes in{" "}
            <a 
              href="https://app.ynab.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-medium underline inline-flex items-center gap-1"
            >
              YNAB <ExternalLink className="h-3 w-3" />
            </a>
            {" "}and re-import your data. Income categories are aggregated into Salary & Bonus, Freelance, and Other Income.
          </p>
        </AlertDescription>
      </Alert>

      {/* Categories by Group */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {["Income", "Bills", "Needs", "Wants"].map((group) => {
          const groupCats = categoriesByGroup[group] || [];

          return (
            <Card key={group} className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2">
                  <FolderTree className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
                  {group}
                  <Badge variant="outline" className={GROUP_COLORS[group]}>
                    {groupCats.length}
                  </Badge>
                </CardTitle>
                {group === "Income" && (
                  <CardDescription className="text-xs">
                    Aggregated from YNAB income sources
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {groupCats.length > 0 ? (
                  <div className="space-y-2">
                    {groupCats.map((cat) => (
                      <div
                        key={cat.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <span className="font-medium text-slate-700">
                          {cat.emoji && `${cat.emoji} `}{cat.name}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">
                    No categories in this group
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {categories.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderTree className="h-12 w-12 text-slate-300 mb-4" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold text-slate-900 mb-1">No categories yet</h3>
            <p className="text-slate-500 mb-4">Import your YNAB data to populate categories</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
